import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  Inject,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { combineLatest, fromEvent, Observable, pipe, race, timer } from 'rxjs';
import {
  concatMap,
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  first,
  map,
  startWith,
  tap
} from 'rxjs/operators';

import { StageV2MetaData, StageView, StageViewDefinition } from 'src/app/api/owcc/models';
import { AppDomContextService } from 'src/app/app-dom-context.service';
import { Mq } from 'src/app/core/config/mq.config';
import { ASSET_SERVER_URL } from 'src/app/core/config/url.config';
import { LazyHostComponent } from 'src/app/core/lazy-host/lazy-host.component';
import {
  AbTestRunnerService,
  STORY_NUMBER_FE_AB_TESTING
} from 'src/app/core/services/ab-testing/ab-test-runner.service';
import { LazyComponentId } from 'src/app/core/services/lazy-components/lazy-components-registry';
import { MediaQueryThreshold } from 'src/app/core/services/match-media/media-query-threshold.enum';
import { TrackClickOnceDirective } from 'src/app/core/services/tracking/directive/track-click-once.directive';
import { TrackingAction } from 'src/app/core/services/tracking/models/tracking-action.enum';
import { TrackingCategory } from 'src/app/core/services/tracking/models/tracking-category.enum';
import { TrackingLabel } from 'src/app/core/services/tracking/models/tracking-label.enum';
import {
  selectIsRowLayoutUsed,
  selectScreenMirroringDevToken,
  selectThresholdRange
} from 'src/app/store/common/common.selectors';
import { selectIsSummaryPage } from 'src/app/store/router/router.selectors';
import { filterTruthy } from 'src/app/utils/functions/rx-filters';
import { WINDOW } from 'src/app/utils/window.token';

import { ImageComponent } from '../../images/image/image.component';
import { OptionalTooltipWrapperComponent } from '../../optional-tooltip-wrapper/optional-tooltip-wrapper.component';
import { TranslatePipe } from '../../pipes/translate/translate.pipe';

import { AwesomeStageStore, SliderComponent } from './awesome-stage-store/awesome-stage.store';
import { awesomeStageComponentAnimations } from './awesome-stage.animations';
import { ButtonTooltipTexts, StageBaseData, StageConfig, StageUpdateData } from './awesome-stage.model';
import { DoubleClickStopPropagationDirective } from './directive/double-click-stop-propagation.directive';
import { PreventTouchStartWhenDisabledDirective } from './directive/prevent-touch-start-when-disabled.directive';
import { TouchStartStopPropagationDirective } from './directive/touch-start-stop-propagation.directive';

export const INTERIOR_THREE_SIXTY_CONTAINER_ID = 'threeSixtyContainer';
export const INTERIOR_THREE_SIXTY_CANVAS_ELEMENT = 'canvas';
const EXTERIOR_THREE_SIXTY_COMPONENT_ID = 'wb-360-viewer';

const STAGE_SLIDER_ICON_SRC = `${ASSET_SERVER_URL}/images/360_slider_stage.svg`;
const INTERIOR_THREE_SIXTY_ICON_SRC = `${ASSET_SERVER_URL}/images/360_indicator.svg`;

const INTERIOR_SLIDER_IN_DOM_DELAY_IN_MS = 400;

const SUMMARY_SIDEBAR_TRACKING_DATA = {
  clickData: {
    label: TrackingLabel.SummarySidebarReturnToConfig,
    category: TrackingCategory.Link,
    action: TrackingAction.SummarySidbar
  }
};

const stageButtonOfferedPostProcessingDueToAppliedAnimations = pipe(
  // debounce and distinct to make sure state is not flaky and triggers ugly animation
  // TODO: investigate if this hardcoded delay is really/still necessary
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  debounceTime(10),
  distinctUntilChanged()
);

@Component({
  selector: 'cc-awesome-stage',
  templateUrl: './awesome-stage.component.html',
  styleUrl: './awesome-stage.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AwesomeStageStore],
  animations: awesomeStageComponentAnimations,
  imports: [
    NgTemplateOutlet,
    LazyHostComponent,
    ImageComponent,
    DoubleClickStopPropagationDirective,
    TrackClickOnceDirective,
    LetDirective,
    OptionalTooltipWrapperComponent,
    TouchStartStopPropagationDirective,
    PreventTouchStartWhenDisabledDirective,
    AsyncPipe,
    TranslatePipe
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AwesomeStageComponent implements AfterViewInit {
  @Output() public readonly stageViewStateChange = this.stageStore.stageViewStateChange$;

  @Output() public readonly interior360DataUrlChange = this.stageStore.interior360DataUrl$;

  @Output() public readonly interiorParameterChange = this.stageStore.interiorParameterChange$;

  @Output() public readonly stageViewChange = this.stageStore.currentView$;

  @Output() public readonly stageHeightChange = this.stageStore.stageHeight$;

  @Output() public readonly fullscreenModeChange = this.stageStore.isInFullscreenMode$;

  @Output() public readonly currentVehicleImageUrl = this.stageStore.currentVehicleExteriorImageUrl$;

  @ViewChild('stageContainer', { read: ElementRef }) public readonly stageContainer!: ElementRef<HTMLElement>;

  @ViewChild('controlBar', { read: ElementRef }) public readonly controlBar!: ElementRef<HTMLElement>;

  @ViewChild('interiorSlider', { read: ElementRef }) public readonly interiorSlider!: ElementRef<SliderComponent>;

  public readonly interiorThreeSixtyContainerId = INTERIOR_THREE_SIXTY_CONTAINER_ID;
  public readonly exteriorThreeSixtyComponentId = EXTERIOR_THREE_SIXTY_COMPONENT_ID;
  public readonly mediaQueryThreshold = MediaQueryThreshold;
  public readonly screenMirroringComponent = LazyComponentId.ScreenMirroring;

  public readonly stageSliderIconSrc = STAGE_SLIDER_ICON_SRC;
  public readonly interiorThreeSixtyIndicatorIconSrc = INTERIOR_THREE_SIXTY_ICON_SRC;

  public readonly summarySidebarTrackingData = SUMMARY_SIDEBAR_TRACKING_DATA;

  public readonly currentFallbackImage$ = this.stageStore.currentFallbackImage$;
  public readonly hasImageError$ = this.stageStore.hasImageError$;
  public readonly isExteriorView$ = this.stageStore.isExteriorView$;
  public readonly isInteriorView$ = this.stageStore.isInteriorView$;
  public readonly isInteriorThreeSixtyView$ = this.stageStore.isInteriorThreeSixtyView$;
  public readonly isUserEmployeeAndIsScreenMirroringActive$ = this.stageStore.isUserEmployeeAndIsScreenMirroringActive$;

  public readonly devUserAccessToken$ = this.store.select(selectScreenMirroringDevToken);

  public readonly exteriorImages$ = this.stageStore.exteriorImages$;
  public readonly exteriorThreeSixtyIndex$ = this.stageStore.exteriorThreeSixtyIndex$;

  public readonly interiorImages$ = this.stageStore.interiorImages$;
  public readonly interiorSliderIndex$ = this.stageStore.interiorSliderIndex$;

  public readonly fullscreenMaxHeightInPx$ = this.stageStore.fullscreenMaxHeightInPx$;

  public readonly stageViewTogglePreviewImage$ = this.stageStore.stageViewTogglePreviewImage$;

  public readonly threeSixtyChangeForCurrentViewOffered$ = this.stageStore.threeSixtyChangeForCurrentViewOffered$?.pipe(
    stageButtonOfferedPostProcessingDueToAppliedAnimations
  );
  public readonly hasInteriorThreeSixtyView$ = this.stageStore.hasInteriorThreeSixtyView$;
  public readonly interiorThreeSixtyStateIcon$ = this.stageStore.interiorThreeSixtyStateIcon$;
  public readonly interiorThreeSixtyTextureUrls$ = this.stageStore.interiorThreeSixtyTextureUrls$;

  public readonly stageViewStateChangeForCurrentViewOffered$ =
    this.stageStore.stageViewStateChangeForCurrentViewOffered$?.pipe(
      stageButtonOfferedPostProcessingDueToAppliedAnimations
    );
  public readonly stageViewStateChangeOfferedInAnyView$ = this.stageStore.stageViewStateChangeOfferedInAnyView$;
  public readonly stageViewStateIcon$ = this.stageStore.stageViewStateIcon$;

  public readonly roofStateChangeForCurrentViewOffered$ = this.stageStore.roofStateChangeForCurrentViewOffered$?.pipe(
    stageButtonOfferedPostProcessingDueToAppliedAnimations
  );
  public readonly roofStateChangeOfferedInAnyView$ = this.stageStore.roofStateChangeOfferedInAnyView$;
  public readonly roofStateIcon$ = this.stageStore.roofStateIcon$;

  public readonly screenMirroringStateIcon$ = this.stageStore.screenMirroringStateIcon$;
  public readonly disclaimerColorStyle$ = this.stageStore.disclaimerColorStyle$;

  public readonly fullscreenModeOffered$ = this.stageStore.fullscreenModeOffered$?.pipe(
    stageButtonOfferedPostProcessingDueToAppliedAnimations
  );
  public readonly fullscreenModeStateIcon$ = this.stageStore.fullscreenModeStateIcon$;
  public readonly isInFullscreenMode$ = this.stageStore.isInFullscreenMode$;
  public readonly isInScreenMirroringMode$ = this.stageStore.isInScreenMirroringMode$;

  public readonly exteriorTransformStyle$ = this.stageStore.exteriorTransformStyle$;
  public readonly exteriorViewThreeSixtyIndicatorTransformStyle$ =
    this.stageStore.exteriorViewThreeSixtyIndicatorTransformStyle$;
  public readonly disclaimer$ = this.stageStore.disclaimer$;

  public readonly interiorThreeSixtyStateButtonTooltip$ = this.stageStore.interiorThreeSixtyStateButtonTooltip$;
  public readonly roofStateButtonTooltip$ = this.stageStore.roofStateButtonTooltip$;
  public readonly stageViewStateButtonTooltip$ = this.stageStore.stageViewStateButtonTooltip$;
  public readonly fullscreenButtonTooltip$ = this.stageStore.fullscreenButtonTooltip$;
  public readonly stageViewToggleButtonTooltip$ = this.stageStore.stageViewToggleButtonTooltip$;
  public readonly trunkButtonTooltip$ = this.stageStore.trunkButtonTooltip$;
  public readonly isFullscreenSupported$ = this.stageStore.isFullscreenSupported$;
  public readonly screenMirroringButtonTooltip$ = this.stageStore.screenMirroringButtonTooltip$;
  public readonly stageViewTrackingData$ = this.stageStore.stageViewTrackingData$;
  public readonly interiorThreeSixtyStateTrackingData$ = this.stageStore.interiorThreeSixtyStateTrackingData$;
  public readonly stageViewStateTrackingData$ = this.stageStore.stageViewStateTrackingData$;
  public readonly roofStateTrackingData$ = this.stageStore.roofStateTrackingData$;
  public readonly fullscreenModeTrackingData$ = this.stageStore.fullscreenModeTrackingData$;
  public readonly screenMirrorTrackingData$ = this.stageStore.screenMirrorTrackingData$;
  public readonly stageExteriorThreeSixtyInteractionTrackingData$ =
    this.stageStore.stageExteriorThreeSixtyInteractionTrackingData$;
  public readonly stageInteriorThreeSixtyInteractionTrackingData$ =
    this.stageStore.stageInteriorThreeSixtyInteractionTrackingData$;
  public readonly trunkButtonTrackingData$ = this.stageStore.trunkButtonTrackingData$;

  public readonly exteriorThreeSixtyRotationSpeedFactor$ = this.stageStore.exteriorThreeSixtyRotationSpeedFactor$;

  public readonly stagePadding$ = this.stageStore.stagePadding$;
  public readonly showExteriorThreeSixtySpinner$ = this.stageStore.showExteriorThreeSixtySpinner$;
  public readonly showInteriorThreeSixtySpinner$ = this.stageStore.showInteriorThreeSixtySpinner$;

  public readonly exteriorThreeSixtyIndicatorTheme$ = this.stageStore.exteriorThreeSixtyIndicatorTheme$;

  public readonly displayTrunkButtonOffered$ = this.stageStore.displayTrunkButtonOffered$?.pipe(
    stageButtonOfferedPostProcessingDueToAppliedAnimations
  );
  public readonly displayTrunkButtonEnabled$ = this.stageStore.displayTrunkButtonEnabled$;

  public readonly displayFullscreenCloseButton$ = this.stageStore.displayFullscreenCloseButton$;

  public readonly isRowLayoutUsed$ = this.store.select(selectIsRowLayoutUsed);

  public readonly currentMq$ = this.store.select(selectThresholdRange).pipe(takeUntilDestroyed(inject(DestroyRef)));

  public readonly isSummaryMobileStageVersionBActive$ = this.abTestRunnerService.shouldBeActiveFor(
    STORY_NUMBER_FE_AB_TESTING.SUMMARY_MOBILE_STAGE,
    'B'
  );

  public readonly isStageActionsHidden$ = combineLatest([
    this.isRowLayoutUsed$,
    this.currentMq$,
    this.isSummaryMobileStageVersionBActive$
  ]).pipe(
    map(
      ([isRowLayoutUsed, currentMq, abTestCaseB]) =>
        currentMq === MediaQueryThreshold.MQ3max && (isRowLayoutUsed || abTestCaseB)
    )
  );

  /* Version without AB-Testing
  public readonly isStageActionsHidden$ = combineLatest([this.isRowLayoutUsed$, this.currentMq$]).pipe(
    map(([isRowLayoutUsed, currentMq]) => isRowLayoutUsed && currentMq === MediaQueryThreshold.MQ3max)
  );
  */

  public readonly displayStageViewToggle$ = combineLatest([
    this.stageStore.displayStageViewToggle$,
    this.isStageActionsHidden$
  ]).pipe(map(([displayStageViewToggle, isStageActionsHidden]) => displayStageViewToggle && !isStageActionsHidden));

  // The interior 360 overlay with indicator is hidden after a timeout of 3 seconds.
  // TODO: investigate if this hardcoded delay is really/still necessary
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  public readonly hideInteriorThreeSixtyOverlayAfterDelay$ = timer(3000).pipe(
    concatMap(() => this.stageStore.showInteriorThreeSixtyOverlay$.pipe(filterTruthy()))
  );

  public readonly hideInteriorThreeSixtyOverlay$ = race([
    this.hideInteriorThreeSixtyOverlayAfterDelay$,
    this.stageStore.hasInteriorThreeSixtyBeenUsed$
  ]).pipe(startWith(false));

  public readonly isSummary$: Observable<boolean> = this.store.select(selectIsSummaryPage);

  public thresholdRange!: MediaQueryThreshold.MQ3max | MediaQueryThreshold.MQ4 | MediaQueryThreshold.MQ5min;

  public constructor(
    private readonly store: Store,
    private readonly stageStore: AwesomeStageStore,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly appDomContext: AppDomContextService,
    private readonly abTestRunnerService: AbTestRunnerService,
    @Inject(WINDOW) private readonly window: Window
  ) {
    /**
     * prevents fullscreen toggle when double clicking on interior slider navigation button.
     * ℹ️ for some reason this _has_ to be in the constructor, ngOnInit does _not_ work!
     */
    const preventFullscreenToggleWhenDoubleClickingOnInteriorSliderNavigationButtons = (): void => {
      // ℹ️ this selector highly depends on the WB component. this might change with future WB updates!
      // idealy we would have an event dispatched from the WB component in order to preventDefault()
      const wbSliderNavigationButtonsQuerySelector = 'wb-slider button';

      const isDoubleClickOnWbSliderNavigationButton = (event: MouseEvent): boolean => {
        const wbSliderNavigationButtonElements = this.appDomContext.shadowRoot.querySelectorAll(
          wbSliderNavigationButtonsQuerySelector
        );

        // eslint-disable-next-line unicorn/prefer-spread
        return Array.from(wbSliderNavigationButtonElements).some(element => event.composedPath().includes(element));
      };

      fromEvent<MouseEvent>(this.elementRef.nativeElement, 'dblclick')
        .pipe(
          filter(event => isDoubleClickOnWbSliderNavigationButton(event)),
          takeUntilDestroyed()
        )
        .subscribe((event: MouseEvent) => {
          event.stopImmediatePropagation();
        });
    };

    preventFullscreenToggleWhenDoubleClickingOnInteriorSliderNavigationButtons();
  }

  @Input()
  public set stageConfig(stageConfig: StageConfig) {
    this.stageStore.updateStageConfig(stageConfig);
  }

  @Input()
  public set stageBaseData(stageBaseData: StageBaseData) {
    /**
     * adds previewImageParameters from interior to interior360 object (if available)
     * this necessary information is (currently) not in the according view definition (Interior360)
     */
    const fixInteriorThreeSixtyObject = (): void => {
      const views = stageBaseData?.metaData?.views;

      if (views?.[StageView.Interior] && views[StageView.Interior360]) {
        const interiorViewDef = views[StageView.Interior];
        const interiorThreeSixtyViewDef = views[StageView.Interior360];

        stageBaseData = {
          ...stageBaseData,
          metaData: {
            ...stageBaseData.metaData,
            views: {
              ...views,
              INTERIOR_360: {
                ...interiorThreeSixtyViewDef,
                previewImageParameters: interiorViewDef?.previewImageParameters
              } as StageViewDefinition
            }
          } as unknown as StageV2MetaData
        };
      }
    };

    if (stageBaseData) {
      fixInteriorThreeSixtyObject();
      this.stageStore.updateStageBaseData(stageBaseData);
    }
  }

  @Input()
  public set stageUpdateData(stageUpdateData: StageUpdateData) {
    if (stageUpdateData) {
      this.stageStore.updateStageUpdateData(stageUpdateData);
    }
  }

  @Input()
  public set mediaQuery(mediaQuery: Mq) {
    this.stageStore.updateMediaQuery(mediaQuery);
  }

  @Input()
  public set buttonTooltipTexts(buttonTooltipTexts: ButtonTooltipTexts) {
    this.stageStore.updateButtonTooltipTexts(buttonTooltipTexts);
  }

  /**
   * toggles fullscreen (also on double click)
   */
  @HostListener('dblclick')
  public toggleFullscreenModeState(): void {
    this.stageStore.toggleFullscreen(this.elementRef.nativeElement);
  }

  public ngAfterViewInit(): void {
    this.stageStore.observeStageHeight(this.stageContainer);
    this.stageStore.observeControlBarHeight(this.controlBar);
    this.stageStore.observeWindowHeight(this.window);
    this.stageStore.registerFullscreenShortcuts(this.elementRef.nativeElement);
  }

  /**
   * toggles exterior / interior
   */
  public toggleView(): void {
    this.stageStore.toggleView();
  }

  /**
   * toggles stage view state (day / night)
   */
  public toggleStageViewState(): void {
    this.stageStore.toggleStageViewState();
  }

  /**
   * toggles roof opened / roof closed
   */
  public toggleRoofState(): void {
    this.stageStore.toggleRoofState();
  }

  /**
   * toggles 360 (interior)
   */
  public toggleInteriorThreeSixty(): void {
    this.stageStore.toggleInteriorThreeSixty();
  }

  public toggleScreenMirroringMode(): void {
    this.stageStore.toggleScreenMirroringMode();
  }

  public onExteriorThreeSixtyRotation(event: CustomEvent<number>): void {
    this.stageStore.onExteriorThreeSixtyStageLoadedChange(true);
    this.stageStore.onExteriorThreeSixtyIndexChange(event.detail);
  }

  public onInteriorChange(event: CustomEvent<number>): void {
    const sliderItem = event.detail;
    const sliderIndex = sliderItem - 1;

    // workaround: index never can be < 0, but it happens on init anyway for an unknown reason, that's why we just return
    if (sliderIndex < 0) {
      return;
    }

    /*
     * ℹ️ Note: we call updateInteriorSliderIndexDebounced instead of updateInteriorSliderIndex for a reason.
     * When we re-enter the slider, the slider does not switch immediately to the desired index but step by step.
     * This makes the wbupdate event fire on the second step already which then again falsify the desired index.
     * Thus we debounce the index update in the store.
     *
     * The WB slider component is still experimental and officially buggy. Once there is another version this might be changed.
     */
    this.stageStore.updateInteriorSliderIndexDebounced(sliderIndex);
  }

  /**
   * Slides to interior trunk image. Switches to according view if required.
   */
  public slideToTrunkImage(): void {
    this.stageStore.isInteriorView$
      .pipe(
        first(),
        tap(isInteriorView => {
          // for now it's sure that we are in any interior view (images or 360), so definitely _not_ in exterior
          if (!isInteriorView) {
            this.stageStore.switchToInteriorView();
          }
        }),
        // small delay for reasons: interior slider has to be available in DOM before we can slide (maybe could also be solved with observing the DOM)
        delay(INTERIOR_SLIDER_IN_DOM_DELAY_IN_MS)
      )
      .subscribe(() => this.stageStore.slideToTrunkImage(this.interiorSlider.nativeElement));
  }

  public onClickReturnToConfiguration(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.stageStore.returnToConfiguration();
  }
}
